import { browser } from "$app/environment";
import { environment } from "$lib/stores/environmentStore";
import { get } from "svelte/store";

type LogLevel = "debug" | "info" | "warn" | "error";
type LogFunction = (...args: unknown[]) => void;

interface LoggerConfig {
  isProduction?: boolean;
  prefix?: string;
  enabledLevels?: LogLevel[];
}

declare global {
  interface Window {
    logger: Logger;
  }
  let logger: Logger;
}

class Logger {
  private static instance: Logger;
  private isProduction: boolean = false;
  private prefix: string = "";
  private enabledLevels: LogLevel[] = [];

  constructor(config: LoggerConfig = {}) {
    if (Logger.instance) {
      return Logger.instance;
    }

    this.prefix = config.prefix ?? "~";
    this.enabledLevels = config.enabledLevels ?? ["debug", "info", "warn", "error"];

    // Initial environment detection
    if (browser) {
      this.isProduction = get(environment) === "production";
      environment.subscribe((env) => {
        this.isProduction = env === "production";
        if (env) {
          console.debug(`Logger env: ${env}`);
        }
      });
    } else {
      this.isProduction = process.env.ENVIRONMENT === "production";
    }

    Logger.instance = this;

    // Safe initial debug message
    if (!this.isProduction) {
      console.debug(`${this.prefix} Logger initialized in ${browser ? "browser" : "server"} mode`);
    }
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isProduction) {
      return false;
    }
    return this.enabledLevels.includes(level);
  }

  private formatMessage(level: LogLevel, args: unknown[]): unknown[] {
    const timestamp = new Date().toISOString();
    return [`${timestamp} ${this.prefix} [${level.toUpperCase()}]`, ...args];
  }

  debug: LogFunction = (...args) => {
    if (this.shouldLog("debug")) {
      console.debug(...this.formatMessage("debug", args));
    }
  };

  log: LogFunction = (...args) => {
    if (this.shouldLog("info")) {
      console.log(...this.formatMessage("info", args));
    }
  };

  info: LogFunction = (...args) => {
    if (this.shouldLog("info")) {
      console.info(...this.formatMessage("info", args));
    }
  };

  warn: LogFunction = (...args) => {
    if (this.shouldLog("warn")) {
      console.warn(...this.formatMessage("warn", args));
    }
  };

  error: LogFunction = (...args) => {
    if (this.shouldLog("error")) {
      console.error(...this.formatMessage("error", args));
    }
  };

  updateConfig(config: LoggerConfig) {
    if (config.isProduction !== undefined) {
      this.isProduction = config.isProduction;
    }
    if (config.prefix) {
      this.prefix = config.prefix;
    }
    if (config.enabledLevels) {
      this.enabledLevels = config.enabledLevels;
    }
  }
}

// Initialize the global logger
const loggerInstance = new Logger();

// Make it available globally
if (typeof window !== "undefined") {
  window.logger = loggerInstance;
}
